import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {
	IGetJoinLeaguesParams,
	ILeague,
	ILeagueAddParams,
	ILeagueDeleteUserParams,
	ILeagueInviteParams,
	ILeagueUpdateParams,
	ILeagueUser,
} from "data/types/leagues";
import type {ILeaguesApiProvider} from "data/providers/api/leagues.api.provider";
import type {IPagination} from "data/types/common";

type IJJoinToLeaguesPagination = IPagination<{
	leagues: ILeague[];
}>;

export interface ILeaguesStore {
	get userLeagues(): ILeague[];
	get joinLeagues(): IJJoinToLeaguesPagination;
	get joinedLeagueIds(): number[];
	get leagueUsers(): IPagination<{users: ILeagueUser[]}>;

	getLeagueById(leagueId: number): ILeague | undefined;
	getLeagueUserById(userId: number): ILeagueUser | undefined;
	fetchLeagues(locale: string): Promise<ILeague[]>;
	fetchLeaguesToJoin(where: IGetJoinLeaguesParams): Promise<void>;
	leaveLeague(leagueId: number, locale: string): Promise<ILeague[]>;
	removeLeague(leagueId: number, locale: string): Promise<void>;
	joinLeague(leagueCode: string, locale: string): Promise<ILeague | undefined>;
	createLeague(params: ILeagueAddParams): Promise<ILeague | undefined>;
	updatePredictorLeague(params: ILeagueUpdateParams): Promise<ILeague | undefined>;
	inviteLeague(params: ILeagueInviteParams): Promise<void>;
	fetchLeague(leagueId: number, locale: string): Promise<ILeague | undefined>;
	fetchLeagueUsers(
		leagueId: number,
		pageSize: number,
		pageNumber: number,
		locale: string,
		newPage?: boolean | undefined
	): Promise<void>;
	clearLeagueUsers(): void;
	clearJoinLeagues(): void;
	removeLeagueUser(params: ILeagueDeleteUserParams): Promise<void>;
	isLeagueJoined(leagueId: number): boolean;
}

@injectable()
export class LeaguesStore implements ILeaguesStore {
	@observable private _userLeagues: ILeague[] = [];
	@observable private _joinLeagues: IJJoinToLeaguesPagination = {
		leagues: [],
		nextPage: false,
	};
	@observable private _joinedLeagueIds: number[] = [];
	@observable private _leagueUsers: IPagination<{users: ILeagueUser[]}> = {
		users: [],
		nextPage: false,
	};

	get userLeagues() {
		return this._userLeagues;
	}

	get joinedLeagueIds() {
		return this._joinedLeagueIds;
	}

	get joinLeagues() {
		return this._joinLeagues;
	}

	get leagueUsers(): IPagination<{users: ILeagueUser[]}> {
		return this._leagueUsers;
	}

	constructor(
		@inject(Bindings.LeaguesApiProvider) private _leaguesApiProvider: ILeaguesApiProvider
	) {
		makeAutoObservable(this);
	}

	clearLeagueUsers(): void {
		this._leagueUsers = {
			users: [],
			nextPage: false,
		};
	}

	@action
	clearJoinLeagues(): void {
		this._joinedLeagueIds = [];
		this._joinLeagues = {
			leagues: [],
			nextPage: false,
		};
	}

	getLeagueById(leagueId: number): ILeague | undefined {
		return this.userLeagues.find((league) => {
			return league?.id === leagueId;
		});
	}

	getLeagueUserById(userId: number): ILeagueUser | undefined {
		return this.leagueUsers.users.find((user) => {
			return user?.userId === userId;
		});
	}

	isLeagueJoined(id: number): boolean {
		return this.joinedLeagueIds.includes(id);
	}

	@action
	async fetchLeagues(locale: string): Promise<ILeague[]> {
		const {data} = await this._leaguesApiProvider.myPredictorLeagues({
			locale,
		});

		runInAction(() => {
			this._userLeagues = data.success.leagues;
		});

		return data.success.leagues;
	}

	@action
	async fetchLeaguesToJoin(where: IGetJoinLeaguesParams) {
		const {data} = await this._leaguesApiProvider.showForJoinPredictorLeague(where);

		const leagueToJoin = data.success;

		if (leagueToJoin) {
			runInAction(
				() =>
					(this._joinLeagues = {
						...leagueToJoin,
						// leagues: [...this._joinLeagues.leagues, ...leagueToJoin.leagues],
					})
			);
		}
	}

	@action
	async createLeague(params: ILeagueAddParams): Promise<ILeague | undefined> {
		const {data} = await this._leaguesApiProvider.create(params);

		runInAction(() => {
			if (data?.success) {
				this._userLeagues.push(data?.success);
			}
		});

		return data?.success;
	}

	@action
	async updatePredictorLeague(params: ILeagueUpdateParams): Promise<ILeague | undefined> {
		const {data} = await this._leaguesApiProvider.updatePredictorLeague(params);

		runInAction(() => {
			if (data?.success) {
				this._userLeagues = this._userLeagues.map((league) => {
					if (league.id === data.success.id) {
						return data.success;
					}
					return league;
				});
			}
		});

		return data?.success;
	}

	@action
	async leaveLeague(leagueId: number, locale: string): Promise<ILeague[]> {
		await this._leaguesApiProvider.leavePredictorLeague({
			locale,
			leagueId,
		});

		runInAction(() => {
			this._userLeagues = this._userLeagues.filter((league) => league.id !== leagueId);
			this._joinedLeagueIds = this._joinedLeagueIds.filter((id) => id !== leagueId);
		});

		return this._userLeagues;
	}

	@action
	async joinLeague(leagueCode: string, locale: string): Promise<ILeague | undefined> {
		const {data} = await this._leaguesApiProvider.joinPredictorLeague({
			code: leagueCode,
			locale,
		});

		runInAction(() => {
			if (data.success) {
				this._userLeagues.push(data.success);
				this._joinedLeagueIds.push(data.success.id);
			}
		});

		return data.success;
	}

	@action
	async inviteLeague(params: ILeagueInviteParams): Promise<void> {
		await this._leaguesApiProvider.invitePredictorLeague(params);
	}

	@action
	async fetchLeague(leagueId: number, locale: string): Promise<ILeague | undefined> {
		const {data} = await this._leaguesApiProvider.getPredictorLeague({
			leagueId,
			locale,
		});

		runInAction(() => {
			if (data) {
				this._userLeagues = [...this._userLeagues, data.success];
			}
		});

		return data.success;
	}

	@action
	async fetchLeagueUsers(
		leagueId: number,
		pageSize: number,
		pageNumber: number,
		locale: string,
		newPage: boolean = false
	) {
		const {data} = await this._leaguesApiProvider.getPredictorLeagueUsers({
			leagueId,
			limit: pageSize,
			page: pageNumber,
			locale,
		});

		runInAction(() => {
			if (data.success?.users) {
				const users = newPage ? this._leagueUsers?.users || [] : [];

				this._leagueUsers = {
					...data.success,
					users: pageNumber ? [...users, ...data.success.users] : data.success.users,
				};
			}
		});
	}

	@action
	async removeLeagueUser(params: ILeagueDeleteUserParams) {
		await this._leaguesApiProvider.removeUserFromPredictorLeague(params);
		const users = this._leagueUsers?.users || [];
		runInAction(() => {
			if (this._leagueUsers) {
				this._leagueUsers.users = users.filter((user) => user.userId !== params.userId);
			}
		});
	}

	@action
	async removeLeague(leagueId: number, locale: string): Promise<void> {
		await this._leaguesApiProvider.removeLeague({leagueId, locale});

		return Promise.resolve();
	}
}
